'use client';

/**
 * ProductDetailCard — full-bleed product brief (canvas/overlay).
 *
 * The expanded view of the rail VendorBriefCard for `sel:product`.
 * Same data source (`graph_product_explore`), same `ENTITY_SCHEMA["product"]`
 * section list, same SectionRouter — the only difference from the
 * rail is that this card mounts in a wider container and arranges
 * sections in a two-column grid that uses the canvas real estate.
 *
 * Mount path: the rail's VendorBriefCard has an [⤢ Expand] affordance
 * that pushes a turn with `renderer_card='product_detail'` into the
 * agent canvas store, opening the concertina overlay. AgentCanvasOverlay
 * looks up `CANVAS_CARD_REGISTRY['product_detail']` and mounts this
 * component. The catalog stays mounted and dimmed underneath.
 *
 * Selection model: this card reads the entity from the agent canvas
 * turn's payload (the rail puts the active product's semantic_id and
 * type on `turn.response.payload`) and falls back to the viewport
 * selection. The fallback exists for cases where the overlay is
 * driven by something other than the rail's expand button (e.g.
 * future Ask Klyde answers that produce a product_detail card).
 *
 * @see klyde_edge/ui_config/composition/cards/vendors.py — content_key="product_detail"
 * @see klyde_edge/ui_config/entity_schema.py — ENTITY_SCHEMA["product"]
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { Loader2, Package } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useAgentCanvasStore } from '@/lib/stores/agent-canvas-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useEntitySchema } from '@/lib/stores/total-matrix-store';
import { text, surfaceBase } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import type { ProductExploreResponse } from '@/lib/api/types';
import type { SectionConfig } from '@/components/copilot/details/sections/types';
import { SectionRouter } from '@/components/copilot/rail-insights/sections/section-router';

const PRODUCT_EXPLORE_STRATEGY = 'graph_product_explore';

// Sections that take the visual lead on the left column at canvas
// scale (hero/gallery, description). Everything else gets the right
// column. The split is purely presentational — the schema is
// untouched; the same SectionRouter dispatches both columns.
const LEFT_COLUMN_TYPES = new Set<SectionConfig['type']>(['image', 'text']);

export function ProductDetailCard() {
  // 1. Source the active entity. The agent canvas turn is the primary
  //    signal (the rail's expand affordance puts payload here). Fall
  //    back to viewport selection so Ask Klyde answers and other turn
  //    sources work too.
  const turn = useAgentCanvasStore((s) => s.turn);
  const payload = (turn?.response as unknown as { payload?: { entity_id?: string; entity_type?: string } } | undefined)
    ?.payload;
  const viewport = useViewportSelection();
  const entityId = payload?.entity_id ?? viewport.entityId ?? '';
  const entityType = payload?.entity_type ?? viewport.entityType ?? 'product';

  const schema = useEntitySchema(entityType);
  const enabled = !!entityId && !!schema && entityType === 'product';

  const { data, isLoading, error } = useStrategyData<ProductExploreResponse>(
    PRODUCT_EXPLORE_STRATEGY,
    enabled ? { semantic_id: entityId } : {},
    enabled,
  );

  if (!enabled) {
    return (
      <div className={cn(text.body, 'p-6 text-muted-foreground')} style={{ fontSize: surfaceBase.canvas }}>
        Select a product to see its details.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12 gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading product details…
      </div>
    );
  }

  const record = (data?.data?.product ?? null) as unknown as Record<string, unknown> | null;

  if (error || !record) {
    return (
      <div className={cn(text.body, 'p-6 text-muted-foreground')} style={{ fontSize: surfaceBase.canvas }}>
        {error ? `Failed: ${(error as Error).message}` : 'No data for this product.'}
      </div>
    );
  }

  const sections = schema!.sections;
  const left: SectionConfig[] = [];
  const right: SectionConfig[] = [];
  for (const s of sections) {
    (LEFT_COLUMN_TYPES.has(s.type) ? left : right).push(s);
  }

  const name = (record.name as string | undefined) ?? 'Product';
  const sku = (record.sku as string | undefined) ?? '';
  const vendor = (record.vendor_name as string | undefined) ?? '';
  const collection = (record.collection_name as string | undefined) ?? '';

  const headerLine = [vendor, collection, sku].filter(Boolean).join('  ·  ');

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ fontSize: surfaceBase.canvas }}>
      {/* Card header — title + vendor / collection / sku one-liner */}
      <header className="flex items-center gap-3 px-6 py-4 border-b border-border">
        <Package className="h-5 w-5 text-muted-foreground" />
        <div className="min-w-0">
          <h2 className={cn(text.title, 'truncate')}>{name}</h2>
          {headerLine && (
            <p className={cn(text.muted, 'truncate')}>{headerLine}</p>
          )}
        </div>
      </header>

      {/* Two-column body — schema-driven sections, split by type */}
      <div className="flex-1 overflow-auto px-6 py-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            {left.map((cfg, i) => (
              <SectionRouter
                key={`L-${cfg.type}-${i}`}
                config={cfg}
                data={record}
                entityType={entityType}
                entityId={entityId}
              />
            ))}
          </div>
          <div className="space-y-4">
            {right.map((cfg, i) => (
              <SectionRouter
                key={`R-${cfg.type}-${i}`}
                config={cfg}
                data={record}
                entityType={entityType}
                entityId={entityId}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
